import React, {useEffect} from 'react';
import {EnrollmentApplication} from 'Enrollment/models/EnrollmentApplication';
import {Accordion, AccordionSummary, Typography, Toolbar, MenuItem} from '@material-ui/core';
import {toFormattedDateTime} from 'Shared/utilities/DateFormatter';
import Grid from '@material-ui/core/Grid';
import {useDispatch, useSelector} from 'react-redux';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import {ModalState, showModal} from 'Core/ducks/modal';
import PublishRoundedIcon from '@material-ui/icons/PublishRounded';
import {Link, useHistory} from 'react-router-dom';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import {
  EnrollmentApplicationTemplateType,
  getEnrollmentFileRecordValidationResult,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {EnrollmentFileStatusType} from 'Enrollment/models/EnrollmentFileStatusType';
import {ReducerState} from 'rootReducer';
import {fold as foldEither} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {renderAsync} from 'Shared/components/RenderAsync';
import {toEither} from 'Core/models/Async';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import * as EnrollmentFileActions from 'Enrollment/ducks/enrollmentFileProcessing';

type GenelcoSpreadsheetFileSummaryAccordionProps = {
  enrollmentFile: EnrollmentApplication;
  enrollmentFileHistory: EnrollmentApplication[];
  fileId: string;
};

const GenelcoSpreadsheetFileSummaryAccordion = (props: GenelcoSpreadsheetFileSummaryAccordionProps) => {
  const {enrollmentFile, fileId} = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const {enrollmentFilesFilter} = useSelector((state: ReducerState) => state.enrollmentFileProcessing);
  let enrollmentFilesFilterData = enrollmentFilesFilter as EnrollmentFileActions.ESREnrollmentFileFilters;

  const {enrollmentFileRecordValidationResult} = useSelector((state: ReducerState) => state.enrollmentFileProcessing);

  useEffect(() => {
    dispatch(getEnrollmentFileRecordValidationResult(fileId!));
    if (enrollmentFilesFilterData.isEnrollmentFileDetails) {
      dispatch(showModal(ModalState.GenelcoEnrollmentApplications({enrollmentFile})));
    }
  }, [dispatch, fileId]);

  return (
    <>
      <Toolbar className="genelco-spreadsheet-file__content-container-header">
        <Typography className="genelco-spreadsheet-file__content-container-header-title" variant="h6">
          File Summary
        </Typography>
        <Tooltip title="Upload File">
          <Link to={EnrollmentRouteConstants.UPLOAD_ENROLLMENT_FILE_APPLICATIONS}>
            <IconButton
              className="btn-upload"
              onClick={() =>
                dispatch(
                  showModal(
                    ModalState.EnrollmentApplicationUpload({
                      fileId: fileId!,
                      groupId: enrollmentFile.groupId!,
                    })
                  )
                )
              }
              aria-label="Upload File"
            >
              <PublishRoundedIcon />
            </IconButton>
          </Link>
        </Tooltip>
        <ActionsMenu>
          <MenuItem
            id="menu-item-simplified-enrollment-application"
            onClick={() => dispatch(showModal(ModalState.GenelcoEnrollmentApplications({enrollmentFile})))}
          >
            View all applications
          </MenuItem>
          <MenuItem
            id="menu-item-simplified-enrollment-application-error"
            onClick={() =>
              history.push(
                EnrollmentRouteConstants.ENROLLMENT_GENELCOSHEET_STATUS_UNSUCCESSFUL_EMPLOYEE_RECORDS(
                  enrollmentFile.id!
                )
              )
            }
          >
            View applications with error(s)
          </MenuItem>
          {enrollmentFile.templateType?.id === EnrollmentApplicationTemplateType.GenelcoSpreadsheet &&
          enrollmentFile.fileStatus?.id !== EnrollmentFileStatusType.COMPLETED &&
          enrollmentFile.fileStatus?.id !== EnrollmentFileStatusType.CANCELED ? (
            <MenuItem
              id="menu-item-simplified-enrollment-application"
              onClick={() =>
                dispatch(
                  showModal(
                    ModalState.SimplifiedEnrollmentFileUploadCancelConfirmation({
                      fileId: fileId!,
                    })
                  )
                )
              }
            >
              Cancel
            </MenuItem>
          ) : (
            ''
          )}
        </ActionsMenu>
      </Toolbar>
      <div className="summary-line-info first-summary-line">
        <Accordion className="summary-line-info__expansion-panel" defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="summary-line-info__expansion-panel__icon-container">
              <i className="icon-container__file-icon"></i>
            </div>
            <div className="summary-line-info__expansion-panel__file-details">
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">File Name:</span>
                <span className="value">{enrollmentFile.fileName}</span>
              </div>

              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Uploaded:</span>
                <span className="value">{toFormattedDateTime(enrollmentFile.fileUploadedDate!)}</span>
              </div>

              <div id="application-count" className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Application Count:</span>
                <a className="value" href="#application-count">
                  {enrollmentFile.rowCount}
                </a>
              </div>
              <div className="summary-line-info__expansion-panel__file-details--content">
                <span className="title">Applications with Errors:</span>
                <span>
                  {enrollmentFile.invalidAuditCount! +
                    enrollmentFile.invalidRecordCount! +
                    enrollmentFile.groupWebComparisonErrorCount!}
                </span>
              </div>
            </div>
          </AccordionSummary>
          <AccordionSummary>
            {pipe(
              toEither(enrollmentFileRecordValidationResult),
              foldEither(renderAsync, enrollmentFileRecordValidationResult => (
                <div className="genelcoSpreedSheet-configDetails">
                  <Grid container spacing={3}>
                    {enrollmentFileRecordValidationResult.coverageTerminationCount! > 0 ? (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <span className="title">Coverage Termination:</span>
                          <span className="value">
                            {enrollmentFileRecordValidationResult.coverageTerminationCount!.toString()}
                          </span>
                        </Grid>
                      </>
                    ) : null}
                    {enrollmentFileRecordValidationResult.coverageElectionCount! > 0 ? (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <span className="title">Coverage Election:</span>
                          <span className="value">
                            {enrollmentFileRecordValidationResult.coverageElectionCount!.toString()}
                          </span>
                        </Grid>
                      </>
                    ) : null}
                    {enrollmentFileRecordValidationResult.coverageChangeCount! > 0 ? (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <span className="title">Coverage Change:</span>
                          <span className="value">
                            {enrollmentFileRecordValidationResult.coverageChangeCount!.toString()}
                          </span>
                        </Grid>
                      </>
                    ) : null}
                    {enrollmentFileRecordValidationResult.reinstatementCount! > 0 ? (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <span className="title">Reinstatement:</span>
                          <span className="value">
                            {enrollmentFileRecordValidationResult.reinstatementCount!.toString()}
                          </span>
                        </Grid>
                      </>
                    ) : null}
                    {enrollmentFileRecordValidationResult.adminChangeCount! > 0 ? (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <span className="title">Admin Change:</span>
                          <span className="value">
                            {enrollmentFileRecordValidationResult.adminChangeCount!.toString()}
                          </span>
                        </Grid>
                      </>
                    ) : null}
                    {enrollmentFileRecordValidationResult.nonCoverageDemographicChangeCount! > 0 ? (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <span className="title">Non-Coverage Demographic Change:</span>
                          <span className="value">
                            {enrollmentFileRecordValidationResult.nonCoverageDemographicChangeCount!.toString()}
                          </span>
                        </Grid>
                      </>
                    ) : null}
                    {enrollmentFileRecordValidationResult.undeterminedCount! > 0 ? (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <span className="title">Undetermined:</span>
                          <span className="value">
                            {enrollmentFileRecordValidationResult.undeterminedCount!.toString()}
                          </span>
                        </Grid>
                      </>
                    ) : null}
                    {enrollmentFileRecordValidationResult.noChangesDetectedCount! > 0 ? (
                      <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                          <span className="title">No Changes Detected:</span>
                          <span className="value">
                            {enrollmentFileRecordValidationResult.noChangesDetectedCount!.toString()}
                          </span>
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                </div>
              ))
            )}
          </AccordionSummary>
        </Accordion>
      </div>
    </>
  );
};

export default GenelcoSpreadsheetFileSummaryAccordion;
